/**
 * Opt-in features — admin side of the beta opt-in system.
 *
 * The frozen contract lives in the BFF repo, at
 * `docs/features/surveys-optin/03-contratos.md` (section `ADMIN · Opt-in`) and
 * `03-tipos.ts`. What a dashboard user receives (`OptInFeaturePublic`) is a translated,
 * per-person projection of {@link OptInFeature}; this file is the admin view, which carries every
 * language and the moderation fields.
 *
 * **There is no exit-questionnaire field here, and that is the shape of the data, not an omission.**
 * The link is written on the survey — `Survey.requiresOptInFeatureId` — and nothing on the feature
 * points back (R23). The reason catalogue that used to live here (`optOutReasons`,
 * `optOutAllowFreeText`, `optOutReasonRequired`) went with BFF 5.0.0: what to ask whoever is leaving
 * is a survey now, with statements, per-question requiredness, versions and translations, instead of
 * one unlabelled radio.
 *
 * @module Pages/BUs/OptInFeatures
 */

/** The three languages every tenant supports. Base columns are English; the rest are translations. */
export const OPT_IN_LANGUAGES = ['en', 'pt', 'es'] as const;

export type OptInLanguage = (typeof OPT_IN_LANGUAGES)[number];

export const OPT_IN_LANGUAGE_LABELS: Record<OptInLanguage, string> = {
  en: 'Inglês',
  pt: 'Português',
  es: 'Espanhol',
};

/**
 * The fifteen texts, in one language.
 *
 * They are grouped by the three surfaces they render on — entry point, enable modal, disable
 * modal — which is also how the form lays them out. Every one is nullable: the dashboard falls
 * back to nothing rather than to invented copy.
 *
 * The four toast texts are here, and the two toast **types** are not: a sentence is read in one
 * language, an enum is not, and which language the reader is in does not decide whether an alert
 * fires. Same split as `badgeTooltip` against {@link OptInFeatureAppearance}.
 */
export interface OptInFeatureTexts {
  /** ① Entry point, in the sidebar. */
  name: string;
  description: string | null;
  /** Label of the control while the feature is OFF — the invitation to try it. */
  enterCta: string | null;
  /** Label of the SAME control while it is ON — the invitation to go back. */
  exitCta: string | null;
  /**
   * Tooltip of the badge on that control (4.2.0 — R46).
   *
   * **The only appearance field with a language**, which is why it sits here with the texts and
   * not in {@link OptInFeatureAppearance}: a glyph and an enum read the same in any language, a
   * sentence does not. `null` is "no tooltip" and does not fall back to the base column.
   */
  badgeTooltip: string | null;
  /** ② Enable modal. */
  confirmTitle: string | null;
  confirmBody: string | null;
  confirmCta: string | null;
  /**
   * ②b Title of the toast raised after the feature is turned ON (R48).
   *
   * **Never `null` while `enableToastType` is set** — the BFF answers `400` on that pair, because a
   * toast with a type and no title renders as an empty box on the customer's screen.
   *
   * The other way round is legitimate and is not an oversight: a title written with the type left
   * `null` is the wording kept with the toast **switched off**, which is what lets somebody turn it
   * on and off again without retyping anything.
   */
  enableToastTitle: string | null;
  /** ②b Body of that toast. Optional even with a type set: a toast with only a title is fine. */
  enableToastBody: string | null;
  /** ③ Disable modal. */
  optOutTitle: string | null;
  optOutBody: string | null;
  optOutCta: string | null;
  /** ③b Title of the toast raised after the feature is turned OFF. Same rules as the enable one. */
  disableToastTitle: string | null;
  disableToastBody: string | null;
}

/** The badges `gl-ui` already draws on a sidebar entry. */
export const OPT_IN_ENTRY_BADGES = ['NONE', 'BETA', 'NEW'] as const;

/**
 * The badge on the entry point, or `null`.
 *
 * An enum rather than `isBeta: boolean`, on purpose: `gl-ui` draws two badges, so a feature
 * graduating out of beta becomes `NEW` by editing one row — no column, no migration, no deploy.
 *
 * **`null` is not `NONE`.** `null` is "nobody decided, let the dashboard pick", which is the
 * default and what every feature registered so far carries; `NONE` is "we decided there is no
 * badge". Collapsing the two would silently strip the Beta badge off every existing feature the
 * first time somebody saved it.
 */
export type OptInEntryBadge = (typeof OPT_IN_ENTRY_BADGES)[number];

/**
 * How the entry point looks — the five fields with no language (4.2.0 — R46).
 *
 * It is **one button with two faces**: off it invites (`enterCta`), on it offers the way back
 * (`exitCta`). So the appearance is per state too, exactly like the labels.
 *
 * `null` everywhere means "the dashboard decides" and is the default: with these empty the
 * dashboard renders precisely what it renders today, so publishing the libraries changes nothing
 * on its own.
 */
export interface OptInFeatureAppearance {
  /** `gl-icons` variant (`Namespace.Glyph`) while the feature is OFF. */
  enterIcon: string | null;
  /** Same, while it is ON. */
  exitIcon: string | null;
  enterBadge: OptInEntryBadge | null;
  exitBadge: OptInEntryBadge | null;
  /**
   * Whether the **invitation** is offered. Never `null`.
   *
   * It governs the OFF face alone: `showsButton = isEnabled ? true : enterVisible`. With the
   * feature ON the button is always drawn, because it is the same button that leads back out, and
   * hiding it there would trap somebody inside the beta — and the way out is exactly where the
   * exit questionnaire collects.
   *
   * Not to be confused with `isActive`: that one drops the feature from the bootstrap and puts
   * **everyone** back on the old behaviour; this one only closes the door to newcomers.
   */
  enterVisible: boolean;
}

/** The four toast types `gl-components` draws, spelled the way the BFF stores them. */
export const OPT_IN_TOAST_TYPES = ['INFO', 'SUCCESS', 'WARNING', 'ERROR'] as const;

/**
 * The kind of toast raised after the toggle, or `null`.
 *
 * The four values map one to one onto `GLToastTypes` (`'info' | 'warning' | 'success' | 'error'`).
 * The BFF answers them in upper case because it is a Prisma enum; lower-casing them is the
 * dashboard's job, not a second list.
 */
export type OptInToastType = (typeof OPT_IN_TOAST_TYPES)[number];

/**
 * Whether each side of the toggle raises a toast (R48) — the two fields with no language.
 *
 * **The type is the switch.** `null` means the toast does not fire, and there is no
 * `hasEnableToast` beside it, on purpose: two fields holding one decision end up disagreeing, and
 * "on, with no type" has no drawing. Turning the toast off on screen is choosing "nenhum" in the
 * select, never wiping the texts — those stay stored, and that is the point.
 *
 * The two sides are independent: a feature may greet whoever turns it on and say nothing to
 * whoever turns it off, or the reverse.
 */
export interface OptInFeatureToasts {
  /** After the `PATCH` with `isEnabled: true` answers `200`. */
  enableToastType: OptInToastType | null;
  /** After the `PATCH` with `isEnabled: false` answers `200` — not after the exit questionnaire. */
  disableToastType: OptInToastType | null;
}

/** A translation row: the fifteen texts, for one language. */
export interface OptInFeatureTranslation extends Partial<OptInFeatureTexts> {
  language: OptInLanguage;
}

/** A use case of the tenant, as the admin payload names it. */
export interface OptInFeatureUseCase {
  id: string;
  slug: string;
  name: string;
}

/**
 * A feature as the admin endpoints answer it.
 *
 * The appearance and toast fields are `Partial` rather than required, for the same reason
 * `enabledNow` is optional: a BFF older than 4.2.0 (toasts: 4.3.0) answers without them, and the
 * form should then open on the defaults instead of on `undefined`.
 */
export interface OptInFeature
  extends OptInFeatureTexts, Partial<OptInFeatureAppearance>, Partial<OptInFeatureToasts> {
  id: string;
  tenantId?: string;
  tenantSlug: string;
  /** Matched by the dashboard against the bootstrap. Immutable once the feature exists. */
  key: string;
  /**
   * Kill switch, not a draft flag.
   *
   * Turning it off drops the feature from every bootstrap on the next load, which puts everyone
   * back on the old behaviour immediately — nobody's individual state is lost, it simply stops
   * being read.
   */
  isActive: boolean;
  /**
   * Which use cases are offered the feature.
   *
   * **Empty means everyone in the tenant**, never nobody: an absent restriction is no
   * restriction. The screen says so, because an empty multi-select reads like the opposite.
   */
  useCases: OptInFeatureUseCase[];
  translations: OptInFeatureTranslation[];
  createdAt: string;
  updatedAt: string;
  /**
   * When the feature was soft-deleted, or `null` while it is live.
   *
   * Only meaningful once the listing was asked for `includeDeleted=true`: it is what separates a
   * deleted row from a live one inside the same list.
   */
  deletedAt?: string | null;
  /**
   * Adoption counters, when the listing carries them.
   *
   * Optional on purpose: an older BFF that answers the listing without them makes the card show
   * a dash instead of claiming nobody turned the feature on.
   */
  enabledNow?: number;
  everEnabled?: number;
}

export interface OptInFeaturePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface OptInFeatureListResponse {
  data: OptInFeature[];
  pagination: OptInFeaturePagination;
}

/**
 * What `POST /:id/purge` answers with — how many rows went, of each kind.
 *
 * `deletedOptIns` counts **everyone who ever opted in**, not the people currently using the
 * feature: the row exists because somebody turned it on at least once. So it matches "Já ligaram"
 * on the adoption screen, never "Ligados agora".
 */
export interface OptInPurgeResult {
  deletedOptIns: number;
  deletedEvents: number;
}

/** What `DELETE /:id/users/:platformUserId` answers with, for one person (R30). */
export interface OptInUserErasure {
  platformUserId: number;
  deletedOptIns: number;
  deletedEvents: number;
  message: string;
}

export interface OptInFeatureListRequest {
  /**
   * Brings the deleted features **into** the listing, next to the live ones. Default `false`.
   *
   * Not "only the deleted": the `includeDeleted` of the e-mail signatures module means the
   * opposite, and reading this one that way would quietly empty the screen.
   */
  includeDeleted?: boolean;
  tenantSlug?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface OptInFeatureCreatePayload
  extends OptInFeatureTexts, OptInFeatureAppearance, OptInFeatureToasts {
  tenantSlug: string;
  key: string;
  /** Sending it replaces the whole set; omitting it leaves the current one alone. */
  useCaseIds?: string[];
  isActive: boolean;
  translations: OptInFeatureTranslation[];
}

/** `key` and `tenantSlug` are absent by design: neither can move after creation. */
export type OptInFeatureUpdatePayload = Partial<
  Omit<OptInFeatureCreatePayload, 'key' | 'tenantSlug'>
>;

export interface OptInAdoptionPoint {
  /** `YYYY-MM-DD`. */
  date: string;
  /** Distinct people who turned it on that day. */
  enabled: number;
  /** Distinct people who turned it off that day. */
  disabled: number;
  /**
   * Times it was turned on that day, alongside `enabled`, which counts the people who did it: one
   * person flipping it three times is `enabled: 1` and `enabledEvents: 3`.
   */
  enabledEvents: number;
  /** Times it was turned off that day. */
  disabledEvents: number;
}

/**
 * The same activity in its two units, so a panel can name the one it is drawing.
 *
 * They diverge exactly when somebody toggles more than once — the case that made the chart read
 * as more adoption than there was.
 */
export interface OptInAdoptionUnits {
  events: number;
  people: number;
}

export interface OptInAdoption {
  featureKey: string;
  featureName?: string;
  /** People with the feature on right now. */
  enabledNow: number;
  /** People who ever turned it on, including those who later left. */
  everEnabled: number;
  /** People who turned it on and then off. */
  reverted: number;
  /** `reverted / everEnabled`, as a fraction. */
  revertRate: number;
  /** People who came back after reverting. */
  reEnabled: number;
  /** Median days between turning it on and giving up. `null` when nobody has reverted. */
  medianDaysUntilRevert: number | null;
  series: OptInAdoptionPoint[];
  /**
   * Every toggle in the window. `people` is **not** the sum of the daily `people`: whoever
   * toggled on two different days is in both and is still one person.
   */
  toggleTotals: OptInAdoptionUnits;
}

/**
 * One person's opt-in history.
 *
 * There is no name and no e-mail, by design: the BFF has no platform-user table and resolving
 * one would be copying personal data for a screen that does not need it.
 */
export interface OptInFeatureUser {
  platformUserId: number;
  isEnabled: boolean;
  firstEnabledAt: string | null;
  lastEnabledAt: string | null;
  disabledAt: string | null;
  enableCount: number;
  disableCount: number;
}

export interface OptInFeatureUsersRequest {
  page?: number;
  limit?: number;
  state?: 'enabled' | 'disabled';
}

export interface OptInFeatureUsersResponse {
  users: OptInFeatureUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OptInFeatureErrorResponse {
  message: string;
  statusCode: number;
}
